import { ReactElement, CSSProperties, Fragment } from "react";
import { AxisTypes, IAxisBand, IAxisLinear, ILineChartConfig, IPoint } from "./types";
import Chart from "./chart";
import { getTicks, useChartDimensions, useUniqueId } from "./chartUtils";
import AxisHorizontal from "./AxisHorizontal";
import AxisVertical from "./AxisVertical";
import * as d3 from "d3";
import Grid from "./grid";
import { formatNumber } from "../../system/utils";
import HtmlTooltip from "./tooltip";
import Legend from "./legend";
import { IDictionary } from "../../types";
import { useTheme } from "@mui/material";


interface ILineChartProps {
  lineChartConfig: ILineChartConfig;
  sx?: CSSProperties;
}

const parseNan = (n: number, v: number) => isNaN(n) ? v : n;

const LineChart = ({ lineChartConfig, sx }: ILineChartProps): ReactElement => {
  const theme = useTheme();

  let marginRight = 10;
  lineChartConfig.yAxis.forEach(x => {
    if (!x.hidden) {
      marginRight += 60;
    }
  });

  const [ref, dimensions] = useChartDimensions<HTMLDivElement>({
    height: lineChartConfig.height ?? undefined,
    width: lineChartConfig.width ?? undefined,
    marginBottom: 40,
    marginLeft: 10,
    marginRight,
    marginTop: lineChartConfig.showLegend  ? 20 : 10
  });

  const lineID = useUniqueId("line-chart");

  const isBandX = "categories" in lineChartConfig.xAxis;

  const xScale = isBandX
    ? d3.scaleBand()
      .domain((lineChartConfig.xAxis as IAxisBand).categories)
      .range([0, dimensions.boundedWidth])
    : d3.scaleLinear()
      .domain([(lineChartConfig.xAxis as IAxisLinear).minimum, (lineChartConfig.xAxis as IAxisLinear).maximum])
      .range([0, dimensions.boundedWidth]);

  const isBandY = "categories" in lineChartConfig.yAxis;

  const yScales: IDictionary<{ axis: d3.ScaleBand<string> | d3.ScaleLinear<number, number>, config: AxisTypes, }> = {};
  lineChartConfig.yAxis.forEach(axis => {
    yScales[axis.name] = {
      axis: isBandY
        ? d3.scaleBand()
          .domain((axis as IAxisBand).categories)
          .range([0, dimensions.boundedWidth])
        : d3.scaleLinear()
          .domain([(axis as IAxisLinear).minimum, (axis as IAxisLinear).maximum])
          .range([dimensions.boundedHeight, 0])
          .nice(),
      config: axis
    };
  });

  if (lineChartConfig.yAxis.length === 0) {
    return <div ref={ref} style={sx} />;
  }

  const xAccessor = (d: IPoint) => d.x;
  const yAccessor = (d: IPoint) => d.y;
  const xAccessorScaled = (d: IPoint) => xScale(xAccessor(d) as any) ?? 0;
  const yAccessorScaled = (key: string, d: IPoint) => yScales[key].axis(yAccessor(d) as any) ?? 0;
  const y0AccessorScaled = (key: string) => yScales[key].axis(yScales[key].axis.domain()[0] as any) ?? 0;

  const xTicks = isBandX ? xScale.domain() : getTicks(xScale.domain() as number[], (lineChartConfig.xAxis as IAxisLinear).ticksCount);
  const yTicks = isBandY ? yScales[lineChartConfig.yAxis[0].name].axis.domain() : getTicks(yScales[lineChartConfig.yAxis[0].name].axis.domain() as number[], (lineChartConfig.yAxis[0] as IAxisLinear).ticksCount);

  const xOffset = isBandX ?  (xScale as d3.ScaleBand<string>).bandwidth() / 2 : 0;

  let yIndex = -1;

  const bandWidth = dimensions.boundedWidth / Math.max(xTicks.length, 1) / 2.0;

  return (
    <div ref={ref} style={sx}>
      <Chart dimensions={dimensions} >
        <AxisHorizontal
          chartID={lineID}
          dimensions={dimensions}
          scale={xScale}
          label={lineChartConfig.xAxis.name}
          ticks={xTicks}
          formatTick={lineChartConfig.xAxis.formatTick}
        />

        { Object.keys(yScales).map((key) => {
          const yScale = yScales[key].axis;
          const yConfig = yScales[key].config;
          const isBand = "categories" in yConfig;
          const ticks = isBand ? yScale.domain() : getTicks(yScale.domain() as number[], (yConfig as IAxisLinear).ticksCount);

          if(yConfig.hidden) {
            return;
          }

          yIndex++;

          return (
            <AxisVertical
              key={`${lineID}-y-axis-${yIndex}`}
              index={yIndex}
              chartID={lineID}
              dimensions={dimensions}
              scale={yScale}
              label={yConfig.name}
              ticks={ticks}
              formatTick={yConfig.formatTick}/>
          );
        })
        }

        <Grid charID={lineID} xScale={xScale} yScale={yScales[lineChartConfig.yAxis[0].name].axis} dimensions={dimensions} xTicks={xTicks} yTicks={yTicks}/>

        { lineChartConfig.showLegend && <Legend chartID={lineID} series={lineChartConfig.series} width={dimensions.boundedWidth} /> }

        <g transform={`translate(${xOffset}, 0)`}>
          {lineChartConfig.series.map((series, index) => {
            const lineGenerator = (series.type === "area" ? d3.area<IPoint>() : d3.line<IPoint>())
              .x(xAccessorScaled)
              .y((d) => yAccessorScaled(series.yAxisName, d))
              .curve(d3.curveLinear);

            const data = series.value as IPoint[];

            const renderArea = () => {
              const areaGenerator = d3.area<IPoint>()
                .x(xAccessorScaled)
                .y((d) => yAccessorScaled(series.yAxisName, d))
                .curve(d3.curveLinear)
                .y0(() => parseNan(y0AccessorScaled(series.yAxisName), 0))
                .y1(d => parseNan(yAccessorScaled(series.yAxisName, d), 0));

              return (
                <path
                  className={"Line"}
                  fill={series.type === "area" ? series.color : undefined}
                  fillOpacity={series.type === "area" ? 0.2 : undefined}
                  strokeWidth={0}
                  d={areaGenerator(data) ?? ""}
                />);
            };

            return (
              <Fragment key={`${lineID}-series-${index}`}>
                { series.type === "area" && renderArea()}
                <path
                  className={"Line"}
                  fill="none"
                  stroke={series.color}
                  strokeWidth={3}
                  d={lineGenerator(data) ?? ""}
                />

                { Array.isArray(series.value) && series.value.map((point, pointIndex) => {
                  const p = point as IPoint;
                  const categoryName = lineChartConfig.xAxis.formatTick ? lineChartConfig.xAxis.formatTick(p.x as number, 1, 0) : p.x;
                  const radius = lineChartConfig.pointStyle ? lineChartConfig.pointStyle.radius : 2;
                  const x = xAccessorScaled(point as IPoint);
                  const y =yAccessorScaled(series.yAxisName, point as IPoint);

                  return(
                    <HtmlTooltip
                      key={`${lineID}-series-${pointIndex}-tooltip`}
                      title={
                        <div className="display-flex-column">
                          <div className="display-flex-column align-items-center" >
                            <strong>{categoryName}</strong>
                          </div>
                          <div className="display-flex-row align-items-center" >
                            <div style={{ background: series.color, width: 10, height: 10, marginRight: 10 }} />
                            <strong className="flex-grow-1 mr-2">{series.name}</strong>
                            <strong className="flex-grow-0">
                              {formatNumber(p.y, 2)}
                              {series.unit && <sub>{`[${series.unit}]`}</sub>}
                            </strong>
                          </div>
                        </div>
                      }>
                      <g>
                        <circle cx={x} cy={y}
                          r={radius} fill={series.color} stroke={series.color} strokeWidth={2}/>

                        <text x={x} y={y - radius - 4}
                          dominantBaseline="auto" textAnchor="middle" fill={theme.palette.text.primary}>
                          {formatNumber(p.y, 0)}
                        </text>

                        {p.limits && p.limits.map((l, i) => {
                          const yl = yScales[series.yAxisName].axis(l.value as any) ?? 0;

                          return (
                            <line  key={`${lineID}-series-${pointIndex}-limit-${i}`}
                              stroke="red" strokeWidth={2}
                              y1={parseNan(yl, 0)}
                              y2={parseNan(yl, 0)}
                              x1={x - bandWidth}
                              x2={x + bandWidth}/>
                          );
                        })}

                      </g>
                    </HtmlTooltip>
                  );
                })}
              </Fragment>);
          })
          }
        </g>
      </Chart>
    </div>);
};

export default LineChart;
